import { useEffect, useState } from 'react';
import { Route, Routes, useNavigate, matchRoutes, useLocation, RouteMatch, Navigate, useResolvedPath } from 'react-router-dom';
import { IAppointment, insertAuditTrail } from '../../API/GoMoto';
import store from 'store2';
import {
    AppointmentDispatchTypes,
    useAppointmentDispatch,
    useAppointmentHistory,
    useAppointmentStateUnsafe,
    useAppointmentUpdateAndPersist,
    useHasAppointment,
    useIsFullMobileCheckInAllowed,
    useTowInProcess,
} from '../../context/Appointment/appointment';
import { Types, useCartDispatch } from '../../context/cart';
import { useFullReset, useKioskState, useTowInSupport } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { Vehicle } from '../Customer/Add/Vehicle';
import { QuickDrive } from '../Hunter/QuickDrive/QuickDrive';
import { Page } from '../Layout/Page/Page';
import { Services } from '../Services/ServicesIndex';
import { XtimeRecommended } from '../Services/XTime/XTimeRecommended';
import { CustomUpsell } from '../Upsell/CustomUpsell';
import { Upsell } from '../Upsell/UpsellIndex';
import { Valuation } from '../Valuation/Index';
import { Complete } from './Complete';
import { ConfirmInformation } from './ConfirmInformation/ConfirmInformation';
import { Customer } from './Customer';
import { KeyDropIndex } from './KeyDrop/KeyDropIndex';
import { Mileage } from './Mileage';
import { RecallsRecommendedLastVisit } from './RecallsRecommendedLastVisit';
import { Search } from './Search';
import { Services as AppointmentServices } from './Services';
import { Summary } from './Summary/Summary';
import { SurveyStep1 } from './SurveyStep1/SurveyStep1';
import { SurveyStep2 } from './SurveyStep2/SurveyStep2';
import { TagNumber } from './TagNumber';
import { Transportation } from './Transportation/Transportation';
import { Welcome } from './Welcome';
import { SignElectronically } from './SignElectronically';
import { DisclaimerTabs } from './DisclaimerTabs';
import { getIsInformationMissing } from '../../helpers/Appointment';
import { AppRoutePaths, AppointmentRoutePaths, CustomerRoutePaths, PathName } from '../../helpers/Routes';
import { AppointmentLookupMethod } from './Search/Lookup';
import { ResetKiosk } from './ResetKiosk';
import { AppointmentRoute } from './AppointmentRoute';
import { TowInAdditionalDetails } from './TowInAdditionalDetails';
import { GMDialogError } from '../../controls/Dialog';
import { CustomerDetails, TowInDetails } from './Details';
import { TowInComplete } from './TowInComplete/TowInComplete';
import { Events } from '../../API/Enums';
import { useNavigateAndRecord } from '../../helpers/Hooks';
import { GoMotoComponent } from '../../API/GoMoto.types';
import { ContinueAtKiosk } from '../ContinueAtKiosk';

export const AppointmentIndex: GoMotoComponent = () => {
    const Localization = useLocalization();
    const towInProcess = useTowInProcess();
    const { pathname } = useResolvedPath('.');
    const navigate = useNavigate();
    const appointment = useAppointmentStateUnsafe();
    const setAppointment = useAppointmentUpdateAndPersist();
    const appointmentDispatch = useAppointmentDispatch();
    const location = useLocation();
    const disableCartRoutes: RouteMatch[] | null = matchRoutes(
        [
            {
                path: `${pathname}`,
            },
            {
                path: `${pathname}/customer`,
            },
            {
                path: `${pathname}/lookup/*`,
            },
            {
                path: `${pathname}/details`,
            },
            {
                path: `${pathname}/mileage`,
            },
            {
                path: `${pathname}/vehicle`,
            },
            {
                path: `${pathname}/survey1`,
            },
            {
                path: `${pathname}/survey2`,
            },
            {
                path: `${pathname}/complete`,
            },
            {
                path: `${pathname}/resend-email`,
            },
            {
                path: `${pathname}/summary`,
            },
            {
                path: `${pathname}/keydrop/*`,
            },
            {
                path: `${pathname}/search/*`,
            },
            {
                path: `${pathname}/towinadditionaldetails`,
            },
            {
                path: `${pathname}/towincomplete`,
            },
        ],
        location,
    );
    const disableCart: boolean = !!disableCartRoutes && disableCartRoutes.length > 0;

    const cartDispatch = useCartDispatch();
    const [seeServiceAdvisor, setSeeServiceAdvisor] = useState(false);
    const reset = useFullReset();
    const isFullMobileCheckInAllowed = useIsFullMobileCheckInAllowed();
    const hasAppointment = useHasAppointment();
    const {
        dealer: {
            valuationPageEnabled = true,
            xtimeEnabled = true,
            consentToSignElectronicallyEnabled,
            retainOldPartsEnabled,
            roBackerEnabled,
            roBackerReviewRequired,
            stateEstimateEnabled,
            miscellaneousEnabled,
            newAppointmentEnabled = true,
            newCustomerEnabled = true,
            oldAppointmentEnabled = true,
            hasExpressOr365,
        },
        kioskType,
    } = useKioskState();

    const towInSupport = useTowInSupport();
    const { insertLastPageVisited } = useAppointmentHistory();
    const { navigateAndRecord, NavigateAndRecord } = useNavigateAndRecord();

    const resetKioskMobileComplete = useFullReset(true, false);

    const onAppointmentSelected = async (selected: IAppointment) => {
        await setAppointment(selected, true);
        appointmentDispatch({
            type: AppointmentDispatchTypes.HasAppointment,
            payload: true,
        });
        if (!isElectron()) {
            insertLastPageVisited(PathName(pathname, AppointmentRoutePaths.Details), selected.key);
        }
        navigate(PathName(pathname, AppointmentRoutePaths.Details));
    };

    const onNotListed = () => {
        if (newAppointmentEnabled) {
            appointmentDispatch({
                type: AppointmentDispatchTypes.HasAppointment,
                payload: false,
            });
            navigate(PathName(AppRoutePaths.Customer, CustomerRoutePaths.Lookup));
        }
        else {
            setSeeServiceAdvisor(true);
        }
    };

    useEffect(() => {
        cartDispatch({
            type: Types.DisplayCartIcon,
            payload: !disableCart,
        });
    }, [disableCart, cartDispatch]);

    return (
        <Page>
            <Routes>
                <Route
                    path={''}
                    element={
                        newAppointmentEnabled ? (
                            oldAppointmentEnabled ? (
                                isElectron() ? (
                                    <Navigate replace to={PathName(pathname, AppointmentRoutePaths.Search, AppointmentLookupMethod.PhoneOrEmail)} />
                                ) : (
                                    <Welcome
                                        appointmentLookup={() => navigate(PathName(pathname, AppointmentRoutePaths.Search))}
                                        newAppointment={() => navigate(PathName(pathname, AppointmentRoutePaths.Customer))}
                                    />
                                )
                            ) : (
                                <Navigate replace to={PathName(pathname, AppointmentRoutePaths.Customer)} />
                            )
                        ) : oldAppointmentEnabled ? (
                            isElectron() ? (
                                <Navigate replace to={PathName(pathname, AppointmentRoutePaths.Search, AppointmentLookupMethod.PhoneOrEmail)} />
                            ) : (
                                <Navigate replace to={PathName(pathname, AppointmentRoutePaths.Search)} />
                            )
                        ) : (
                            <ResetKiosk />
                        )
                    }
                />
                <Route
                    path={PathName(AppointmentRoutePaths.Customer, '*')}
                    element={
                        newAppointmentEnabled ? (
                            newCustomerEnabled ? (
                                <Customer />
                            ) : (
                                <Navigate replace to={PathName(AppRoutePaths.Customer, CustomerRoutePaths.Lookup)} />
                            )
                        ) : (
                            <Navigate replace to={pathname} />
                        )
                    }
                />
                <Route
                    path={PathName(AppointmentRoutePaths.Search, '*')}
                    element={
                        oldAppointmentEnabled ? (
                            <Search onAppointmentSelected={onAppointmentSelected} onNotListed={onNotListed} />
                        ) : (
                            <Navigate replace to={pathname} />
                        )
                    }
                />
                <Route
                    path={PathName(AppointmentRoutePaths.Confirm, ':apptKey', '*')}
                    element={
                        <ConfirmInformation
                            onInformationCorrect={(nextUrl?: string) => {
                                appointmentDispatch({ type: AppointmentDispatchTypes.DeserializePreCheckIn });
                                navigateAndRecord(nextUrl ? nextUrl : PathName(pathname, AppointmentRoutePaths.Mileage));
                            }}
                            onInformationIncorrect={() => {
                                if (isFullMobileCheckInAllowed) {
                                    navigate(PathName(AppRoutePaths.Customer, CustomerRoutePaths.Lookup));
                                }
                                else {
                                    navigate(PathName(pathname, AppointmentRoutePaths.Search));
                                }
                            }}
                        />
                    }
                />
                {appointment && (!getIsInformationMissing(appointment) || towInProcess) && (
                    <>
                        <Route
                            path={PathName(AppointmentRoutePaths.Details, '*')}
                            element={
                                <AppointmentRoute>
                                    {towInProcess ? (
                                        <TowInDetails
                                            onProceed={() => {
                                                if (!appointment.vin || !appointment.model || !appointment.make || !appointment.year) {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Vehicle));
                                                }
                                                else {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Mileage));
                                                }
                                            }}
                                        />
                                    ) : (
                                        <CustomerDetails
                                            onProceed={() => {
                                                if (!appointment.vin || !appointment.model || !appointment.make || !appointment.year) {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Vehicle));
                                                }
                                                else {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Mileage));
                                                }
                                            }}
                                        />
                                    )}
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.Vehicle, '*')}
                            element={
                                <AppointmentRoute>
                                    <Vehicle
                                        onProceed={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Mileage));
                                        }}
                                        asWizard={isElectron()}
                                    />
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.Mileage, '*')}
                            element={
                                <AppointmentRoute>
                                    <Mileage
                                        onProceed={() => {
                                            if (appointment.mobileCheckInComplete) {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Hunter));
                                            }
                                            else {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Tagnumber));
                                            }
                                        }}
                                        hideVin={true}
                                    />
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.Tagnumber, '*')}
                            element={
                                <AppointmentRoute>
                                    <TagNumber
                                        onProceed={() => {
                                            if (appointment.mobileCheckInComplete) {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Summary));
                                            }
                                            else {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.ServicesPreviouslyAdded));
                                            }
                                        }}
                                        onSkip={() => {
                                            if (appointment.mobileCheckInComplete) {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Summary), { replace: true });
                                            }
                                            else {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.ServicesPreviouslyAdded), { replace: true });
                                            }
                                        }}
                                    />
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.ServicesPreviouslyAdded, '*')}
                            element={
                                <AppointmentRoute>
                                    {hasAppointment ? (
                                        <AppointmentServices
                                            onProceed={() => {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Valuation));
                                            }}
                                        />
                                    ) : (
                                        <NavigateAndRecord replace to={PathName(pathname, AppointmentRoutePaths.Valuation)} />
                                    )}
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.Valuation, '*')}
                            element={
                                <AppointmentRoute>
                                    {valuationPageEnabled ? (
                                        <Valuation
                                            onProceed={() => {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Recalls));
                                            }}
                                            onSkip={() => {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Recalls), { replace: true });
                                            }}
                                        />
                                    ) : (
                                        <NavigateAndRecord replace to={PathName(pathname, AppointmentRoutePaths.Recalls)} />
                                    )}
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.Recalls, '*')}
                            element={
                                <AppointmentRoute>
                                    <RecallsRecommendedLastVisit
                                        onProceed={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Hunter));
                                        }}
                                        onSkip={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Hunter), { replace: true });
                                        }}
                                    />
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.Hunter, '*')}
                            element={
                                <AppointmentRoute>
                                    {(isElectron() || isFullMobileCheckInAllowed) && appointment.vin ? (
                                        <QuickDrive
                                            onProceed={() => {
                                                if (appointment.mobileCheckInComplete) {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Tagnumber));
                                                }
                                                else {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.AdditionalServices));
                                                }
                                            }}
                                            onSkip={() => {
                                                if (appointment.mobileCheckInComplete) {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Tagnumber), { replace: true });
                                                }
                                                else {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.AdditionalServices), { replace: true });
                                                }
                                            }}
                                        />
                                    ) : (
                                        <NavigateAndRecord
                                            replace
                                            to={PathName(
                                                pathname,
                                                appointment.mobileCheckInComplete ? AppointmentRoutePaths.Tagnumber : AppointmentRoutePaths.AdditionalServices,
                                            )}
                                        />
                                    )}
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.AdditionalServices, '*')}
                            element={
                                <AppointmentRoute>
                                    <Upsell
                                        key={'Upsells-AdditionalServices'}
                                        onProceed={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.SelectAccessories));
                                        }}
                                        onSkip={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.SelectAccessories), { replace: true });
                                        }}
                                    />
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.SelectAccessories, '*')}
                            element={
                                <AppointmentRoute>
                                    <Upsell
                                        key={'Upsells-SelectAccessories'}
                                        partB
                                        onProceed={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.CustomService));
                                        }}
                                        onSkip={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.CustomService), { replace: true });
                                        }}
                                    />
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.CustomService, '*')}
                            element={
                                <AppointmentRoute>
                                    <CustomUpsell
                                        onProceed={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Xtime));
                                        }}
                                        onSkip={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Xtime), { replace: true });
                                        }}
                                    />
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.Xtime, '*')}
                            element={
                                <AppointmentRoute>
                                    {(appointment.vin && xtimeEnabled ? (
                                        <XtimeRecommended
                                            onProceed={() => {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Services));
                                            }}
                                            onSkip={() => {
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Services), { replace: true });
                                            }}
                                        />
                                    ) : (
                                        <NavigateAndRecord replace to={PathName(pathname, AppointmentRoutePaths.Services)} />
                                    ))}
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.Services, '*')}
                            element={
                                <AppointmentRoute>
                                    <Services
                                        onProceed={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Transportation));
                                        }}
                                        onSkip={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Transportation), { replace: true });
                                        }}
                                    />
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.Transportation, '*')}
                            element={
                                <AppointmentRoute>
                                    <Transportation
                                        onProceed={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.SignElectronically));
                                        }}
                                        onSkip={() => {
                                            navigateAndRecord(PathName(pathname, AppointmentRoutePaths.SignElectronically), { replace: true });
                                        }}
                                    />
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.SignElectronically, '*')}
                            element={
                                <AppointmentRoute>
                                    {consentToSignElectronicallyEnabled ? (
                                        <SignElectronically
                                            onProceed={() => {
                                                if (!isElectron() && !isFullMobileCheckInAllowed) {
                                                    appointmentDispatch({ type: AppointmentDispatchTypes.SerializePreCheckIn });
                                                }
                                                navigateAndRecord(PathName(pathname, AppointmentRoutePaths.DisclaimerTabs));
                                            }}
                                        />
                                    ) : (
                                        <NavigateAndRecord replace to={PathName(pathname, AppointmentRoutePaths.DisclaimerTabs)} />
                                    )}
                                </AppointmentRoute>
                            }
                        />
                        <Route
                            path={PathName(AppointmentRoutePaths.DisclaimerTabs, '*')}
                            element={
                                <AppointmentRoute>
                                    {retainOldPartsEnabled || (roBackerEnabled && roBackerReviewRequired) || stateEstimateEnabled || miscellaneousEnabled ? (
                                        <DisclaimerTabs
                                            onProceed={() => {
                                                if (!isElectron() && !isFullMobileCheckInAllowed) {
                                                    appointmentDispatch({ type: AppointmentDispatchTypes.SerializePreCheckIn });
                                                }
                                                navigateAndRecord(
                                                    PathName(pathname, isElectron() || isFullMobileCheckInAllowed ? AppointmentRoutePaths.Summary : AppointmentRoutePaths.Complete),
                                                );
                                            }}
                                        />
                                    ) : (
                                        <NavigateAndRecord
                                            replace
                                            to={PathName(pathname, isElectron() || isFullMobileCheckInAllowed ? AppointmentRoutePaths.Summary : AppointmentRoutePaths.Complete)}
                                        />
                                    )}
                                </AppointmentRoute>
                            }
                        />
                        {isElectron() || isFullMobileCheckInAllowed ? (
                            <>
                                {
                                    towInSupport &&
                                    <Route
                                        path={PathName(AppointmentRoutePaths.TowInAdditionalDetails, '*')}
                                        element={
                                            <AppointmentRoute>
                                                <TowInAdditionalDetails
                                                    onProceed={() => {
                                                        navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Keydrop));
                                                    }}
                                                />
                                            </AppointmentRoute>
                                        }
                                    />
                                }
                                <Route
                                    path={PathName(AppointmentRoutePaths.Summary, '*')}
                                    element={
                                        <AppointmentRoute>
                                            <Summary
                                                onProceed={() => {
                                                    if (!isElectron() && !hasExpressOr365 && kioskType !== 'outdoor' && kioskType !== 'express') {
                                                        navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Survey1));
                                                    }
                                                    else {
                                                        navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Keydrop));
                                                    }
                                                }}
                                            />
                                        </AppointmentRoute>
                                    }
                                />
                                <Route
                                    path={PathName(AppointmentRoutePaths.Keydrop, '*')}
                                    element={
                                        <AppointmentRoute>
                                            <KeyDropIndex
                                                onProceed={() => {
                                                    if (towInProcess) {
                                                        navigateAndRecord(PathName(pathname, AppointmentRoutePaths.TowInComplete));
                                                    }
                                                    else {
                                                        navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Survey1));
                                                    }
                                                }}
                                                onSkip={() => {
                                                    if (towInProcess) {
                                                        navigateAndRecord(PathName(pathname, AppointmentRoutePaths.TowInComplete), { replace: true });
                                                    }
                                                    else {
                                                        navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Survey1), { replace: true });
                                                    }
                                                }}
                                            />
                                        </AppointmentRoute>
                                    }
                                />
                                <Route
                                    path={PathName(AppointmentRoutePaths.Survey1, '*')}
                                    element={
                                        <AppointmentRoute>
                                            <SurveyStep1
                                                onProceed={() => {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Survey2));
                                                }}
                                                onSkip={() => {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Complete), { replace: true });
                                                }}
                                            />
                                        </AppointmentRoute>
                                    }
                                />
                                <Route
                                    path={PathName(AppointmentRoutePaths.Survey2, '*')}
                                    element={
                                        <AppointmentRoute>
                                            <SurveyStep2
                                                onProceed={() => {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Complete));
                                                }}
                                                onSkip={() => {
                                                    navigateAndRecord(PathName(pathname, AppointmentRoutePaths.Complete), { replace: true });
                                                }}
                                            />
                                        </AppointmentRoute>
                                    }
                                />
                                <Route
                                    path={PathName(AppointmentRoutePaths.Complete, '*')}
                                    element={
                                        <AppointmentRoute>
                                            <Complete onComplete={reset} />
                                        </AppointmentRoute>
                                    }
                                />
                                <Route
                                    path={PathName(AppointmentRoutePaths.TowInComplete, '*')}
                                    element={
                                        <AppointmentRoute>
                                            <TowInComplete onComplete={() => {
                                                if (!store.session.get('checkInCompleteLogged')) {
                                                    insertAuditTrail(appointment.key, Events.Check_In_Complete);
                                                }
                                                store.session.remove('checkInCompleteLogged');
                                                resetKioskMobileComplete();
                                            }}
                                            />
                                        </AppointmentRoute>
                                    }
                                />
                            </>
                        ) : (
                            <Route
                                path={PathName(AppointmentRoutePaths.Complete, '*')}
                                element={
                                    <AppointmentRoute>
                                        <ContinueAtKiosk
                                            onComplete={() => {
                                                navigate(`${AppRoutePaths.ImHere}?apptkey=${appointment.key}`);
                                            }}
                                        />
                                    </AppointmentRoute>
                                }
                            />
                        )}
                    </>
                )}
                <Route path={'*'} element={<Navigate replace to={'/'} />} />
            </Routes>
            <GMDialogError
                ariaLabel={Localization.AnErrorHasOccured}
                open={seeServiceAdvisor}
                onClose={reset}
            >
                {Localization.PleaseSeeAServiceAdvisorToCheckIn}
            </GMDialogError>
        </Page>
    );
};